/**
 * English language pack (ENGINE-SPEC §2.12). Tokeniser (§2.5), splitter (§2.6), abbreviations and
 * function words (v3 data files), channel applicability, and the lazily compiled lexicons (`compiledEnglish`).
 */
import { CHANNEL_IDS, type ChannelId } from '../../channels/types.ts';
import { TOK_WORD } from '../../document/types.ts';
import { type CompiledEnglish, compileEnglishLexicons } from '../../lexicons/compile.ts';
import { scriptCensus } from '../script.ts';
import { TokenStore } from '../tokens.ts';
import type {
	BlockTokens,
	ChannelSupport,
	LanguagePack,
	LexiconData,
	SentenceSink,
	TokenBuffer,
} from '../types.ts';
import { lexiconVersion, loadEnglishLexicons } from './lexicons/index.ts';
import { type SplitData, lowerWriterMode, splitEnglish } from './split.ts';
import { tokeniseEnglish } from './tokenise.ts';
import abbrevData from './v3/abbrev.json' with { type: 'json' };
import functionWordData from './v3/function-words.json' with { type: 'json' };

const strong: ReadonlySet<string> = new Set(abbrevData.entries);
const numeric: ReadonlySet<string> = new Set(abbrevData.numericOnly);
const abbreviations: ReadonlySet<string> = new Set([...strong, ...numeric]);
const functionWords: ReadonlySet<string> = new Set(functionWordData.words);
const splitData: SplitData = { strong, numeric };

/** §2.12: language-independent channels (punctuation, rhythm, structure, recurrence, markup). */
export const AGNOSTIC_CHANNELS: ReadonlySet<ChannelId> = new Set<ChannelId>([
	'PT-01',
	'PT-02',
	'RH-01',
	'RH-02',
	'RH-03',
	'SR-01',
	'PROC-01',
	'PROC-02',
	'PROC-05',
	'PROC-06',
	'PROC-07',
	'SF-05',
	'OB-01',
]);

const channels = {} as Record<ChannelId, ChannelSupport>;
for (const id of CHANNEL_IDS) channels[id] = AGNOSTIC_CHANNELS.has(id) ? 'agnostic' : 'native';

function functionWordShare(sample: string): number {
	const store = new TokenStore(Math.min(4096, sample.length + 16));
	tokeniseEnglish(sample, 0, store);
	let words = 0;
	let fw = 0;
	const flag = new Map<number, boolean>();
	for (let i = 0; i < store.count; i++) {
		if (store.cls[i] !== TOK_WORD) continue;
		words++;
		const id = store.id[i] as number;
		let f = flag.get(id);
		if (f === undefined) {
			f = functionWords.has(store.fold(id));
			flag.set(id, f);
		}
		if (f) fw++;
	}
	return words === 0 ? 0 : fw / words;
}

let compiledCache: CompiledEnglish | undefined;

/** The English lexicons compiled to matchers (ENGINE-SPEC §5). Built once, on first use; shared by every channel. */
export function compiledEnglish(): CompiledEnglish {
	compiledCache ??= compileEnglishLexicons(loadEnglishLexicons());
	return compiledCache;
}

let packLexiconsCache: Record<string, LexiconData> | undefined;

/**
 * The pack's `lexicons` table: the raw data files by name (as loaded by `loadEnglishLexicons`) plus
 * `compiled`, the matchers built from them. Built lazily (the document model never needs it).
 */
function packLexicons(): Record<string, LexiconData> {
	if (packLexiconsCache) return packLexiconsCache;
	const raw = loadEnglishLexicons();
	const out: Record<string, LexiconData> = {};
	for (const [k, v] of Object.entries(raw)) {
		if (typeof v === 'object' && v !== null) out[k] = v as unknown as LexiconData;
	}
	out.compiled = {
		version: lexiconVersion,
		source: 'lang/en/lexicons',
		matchers: compiledEnglish(),
	};
	packLexiconsCache = out;
	return out;
}

export const englishPack: LanguagePack = {
	code: 'en',
	detect(sample: string): number {
		const census = scriptCensus(sample);
		if (census.letters === 0) return 0;
		return Math.min(1, functionWordShare(sample) * census.latinShare);
	},
	functionWordShare,
	tokenise(block: string, offset: number, out: TokenBuffer): void {
		tokeniseEnglish(block, offset, out);
	},
	splitSentences(block: BlockTokens, out: SentenceSink): void {
		splitEnglish(block, out, splitData);
	},
	writerMode(blocks: readonly BlockTokens[]): boolean {
		return lowerWriterMode(blocks, splitData);
	},
	abbreviations,
	numericAbbreviations: numeric,
	functionWords,
	// Lexicons (TELLS, PHRASES, SCAFFOLDS, …) and their compiled matchers, built on first access.
	get lexicons(): Record<string, LexiconData> {
		return packLexicons();
	},
	loadLexicons: undefined,
	channels,
	// Priors live on the channel definitions for now (channels/); this table is the per-pack override hook.
	priors: {},
};

export default englishPack;
